import type { MediaLibraryItemSaveParams } from '$/api/server.types';
import { useLanguageStore } from '$/store/languageStore';
import type { User } from '$/store/useOfficialStore/types';

const ASSET_STORAGE_HIGHEST_LEVEL = 6;
const BYTES_PER_M = 1024 ** 2;
const BYTES_PER_G = 1024 ** 3;
const BYTES_PER_T = 1024 ** 4;

export interface AssetStorageInfo {
  usedBytes?: number;
  limitBytes?: number;
  usageLabel: string;
  progressPercent: number;
  isReady: boolean;
  isExceeded: boolean;
  isHighestLevel: boolean;
}

export type AssetStorageProgressBarStyle = {
  width: string;
  minWidth?: number;
};

export type AssetStorageWriteCheckResult =
  | { status: 'pass'; info: AssetStorageInfo; }
  | { status: 'exceeded'; info: AssetStorageInfo; }
  | { status: 'unavailable'; info: AssetStorageInfo; };

/** 获取容量升级 CTA 文案，调用时读取当前语言，避免模块初始化时固定文案。 */
export function getAssetStorageLimitUpgradeText(): string {
  return useLanguageStore.getState().languageStore.smartcatTranslationText(
    (v) => v.upgrade_your_membership_to_unlock_more_storage_space_,
  );
}

/** 获取资产库空间校验失败提示，调用时读取当前语言，避免模块初始化时固定文案。 */
export function getAssetStorageCheckFailedText(): string {
  return useLanguageStore.getState().languageStore.smartcatTranslationText((v) => v.asset_space_check_fail);
}

/** 将后端容量字段规整为非负字节数，异常值按缺失处理。 */
export function normalizeAssetStorageBytes(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
}

/** 将容量按产品规则格式化：依次展示 M、G、T，最多保留 1 位小数。 */
export function formatAssetStorageGB(value: unknown): string {
  const bytes = normalizeAssetStorageBytes(value) ?? 0;
  let unit = 'M';
  let divisor = BYTES_PER_M;
  if (bytes >= BYTES_PER_T) {
    unit = 'T';
    divisor = BYTES_PER_T;
  } else if (bytes >= BYTES_PER_G) {
    unit = 'G';
    divisor = BYTES_PER_G;
  }
  const displayValue = (bytes / divisor).toFixed(1).replace(/\.?0+$/, '');
  return `${displayValue}${unit}`;
}

/** 从会员信息中解析资产库容量展示与 CTA 判断所需状态。 */
export function getAssetStorageInfo(subscribe?: User['subscribe']): AssetStorageInfo {
  const usedBytes = normalizeAssetStorageBytes(subscribe?.usedStorage);
  const limitBytes = normalizeAssetStorageBytes(subscribe?.storageLimit);
  const hasStorageData = usedBytes != null && limitBytes != null;
  const isReady = hasStorageData && limitBytes > 0;
  const rawProgress = isReady ? (usedBytes / limitBytes) * 100 : 0;
  return {
    usedBytes,
    limitBytes,
    isReady,
    usageLabel: hasStorageData ? `${formatAssetStorageGB(usedBytes)}/${formatAssetStorageGB(limitBytes)}` : '',
    progressPercent: Math.max(0, Math.min(rawProgress, 100)),
    isExceeded: hasStorageData ? usedBytes >= limitBytes : false,
    isHighestLevel: Number(subscribe?.level) === ASSET_STORAGE_HIGHEST_LEVEL,
  };
}

/** 生成进度条样式：极小占比但已有容量时给最小可见宽度，避免看起来像 0。 */
export function getAssetStorageProgressBarStyle(info: AssetStorageInfo): AssetStorageProgressBarStyle {
  return {
    width: `${info.progressPercent}%`,
    ...(info.usedBytes != null && info.usedBytes > 0 && info.progressPercent > 0 ? { minWidth: 2 } : {}),
  };
}

/** 校验本次写入是否可执行，容量或本次大小缺失时按产品要求阻断写入。 */
export function checkAssetStorageBeforeWrite(options: {
  incomingBytes?: number;
  subscribe?: User['subscribe'];
}): AssetStorageWriteCheckResult {
  const { incomingBytes, subscribe } = options;
  const info = getAssetStorageInfo(subscribe);
  const size = normalizeAssetStorageBytes(incomingBytes);
  if (size == null || size <= 0 || info.usedBytes == null || info.limitBytes == null) {
    return { status: 'unavailable', info };
  }
  // 非会员可能返回 0 容量限制；这属于可判定的超容，不应落到“校验失败”兜底。
  if (info.limitBytes <= 0 || info.usedBytes + size > info.limitBytes) {
    return { status: 'exceeded', info };
  }
  return { status: 'pass', info };
}

/** 判断本次写入是否会超过资产库容量，供只关心超容状态的旧调用方复用。 */
export function willAssetStorageExceedLimit(options: {
  incomingBytes?: number;
  subscribe?: User['subscribe'];
}): boolean {
  return checkAssetStorageBeforeWrite(options).status === 'exceeded';
}

/** 汇总素材库保存参数中的主文件大小；任一素材大小缺失时返回 undefined 触发写入校验失败。 */
export function sumMediaLibraryItemSaveFileSize(paramsList: MediaLibraryItemSaveParams[]): number | undefined {
  let total = 0;
  for (const params of paramsList) {
    const size = normalizeAssetStorageBytes(params.file_size);
    if (size == null || size <= 0) return undefined;
    total += size;
  }
  return total;
}
